/**
 * @typedef {{
 *   id: string;
 *   chunkKey: string;
 *   doorX: number;
 *   doorY: number;
 *   doorZ: number;
 *   doorRotY: number;
 *   interiorSeed: number;
 *   designIndex: number;
 * }} BuildingEntranceRecord
 */

/**
 * World-space door registry filled when streamed chunks build enterable facades.
 */
export class BuildingEntranceRegistry {
  constructor() {
    /** @type {Map<string, BuildingEntranceRecord>} */
    this._byId = new Map();
    /** @type {Map<string, Set<string>>} */
    this._byChunk = new Map();
  }

  /** @param {BuildingEntranceRecord} record */
  register(record) {
    this._byId.set(record.id, record);
    let set = this._byChunk.get(record.chunkKey);
    if (!set) {
      set = new Set();
      this._byChunk.set(record.chunkKey, set);
    }
    set.add(record.id);
  }

  /** @param {string} chunkKey */
  unregisterChunk(chunkKey) {
    const set = this._byChunk.get(chunkKey);
    if (!set) return;
    for (const id of set) {
      this._byId.delete(id);
    }
    this._byChunk.delete(chunkKey);
  }

  /** @param {string} id */
  get(id) {
    return this._byId.get(id) ?? null;
  }

  /**
   * @param {number} x
   * @param {number} z
   * @param {number} maxRadius
   * @returns {BuildingEntranceRecord | null}
   */
  findNearest(x, z, maxRadius) {
    const maxR2 = maxRadius * maxRadius;
    let best = null;
    let bestD2 = maxR2;

    for (const record of this._byId.values()) {
      const dx = record.doorX - x;
      const dz = record.doorZ - z;
      const d2 = dx * dx + dz * dz;
      if (d2 <= bestD2) {
        bestD2 = d2;
        best = record;
      }
    }
    return best;
  }

  get count() {
    return this._byId.size;
  }
}
